import ExternalIconLink from "components/links/ExternalIconLink";
import { Link, navigate } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { socialMedia } from "components/footer-social-media";
import RiveComponent from "@rive-app/react-canvas";
import { useTranslation } from "react-i18next";
import { useLocation } from "@reach/router";
import { ChevronDownIcon } from "@heroicons/react/24/solid";

interface FooterLinkProps {
    localised?: boolean;
    language?: string;
    isPageAuth?: boolean;
    isPagePhotosIndex?: boolean;
}

const FooterLinks: React.FC<FooterLinkProps> = ({
    localised,
    language,
    isPageAuth,
    isPagePhotosIndex,
}) => {
    const { t, i18n } = useTranslation();

    const shouldFooterBeBlack = isPageAuth || !isPagePhotosIndex;

    const row2Links: ILinkProps[] = [
        {
            heading: t("FOOTER_ABOUT"),
            links: [
                {
                    text: t("FOOTER_ABOUT_US"),
                    href: `${i18n.language === "en" ? "/about" : `/${i18n.language}/about`}`,
                },
                {
                    text: t("FOOTER_CONTACT"),
                    href: "/contact",
                },
                {
                    text: t("FOOTER_MEDIA_KIT"),
                    href: "/media-kit",
                },
                {
                    text: t("FOOTER_FRIENDS"),
                    href: "/friends",
                },
                {
                    text: t("FOOTER_TALKS"),
                    href: "/talks",
                },
                {
                    text: t("FOOTER_SHOP"),
                    href: "https://shop.ente.io",
                    target: "_blank",
                },
            ],
        },
        {
            heading: t("FOOTER_PRODUCTS"),
            links: [
                {
                    text: t("FOOTER_PHOTOS"),
                    href: `${i18n.language === "en" ? "/" : `/${i18n.language}`}`,
                },
                { text: t("FOOTER_AUTH"), href: "/auth", target: "_blank" },
                { text: t("FOOTER_LEGACY"), href: "/blog/legacy" },
            ],
        },
        {
            heading: t("FOOTER_OPEN_SOURCE"),
            links: [
                {
                    text: t("FOOTER_MOBILE"),
                    href: "https://github.com/ente-io/ente/tree/main/mobile#readme",
                    target: "_blank",
                },
                {
                    text: t("FOOTER_WEB"),
                    href: "https://github.com/ente-io/ente/tree/main/web#readme",
                    target: "_blank",
                },
                {
                    text: t("FOOTER_DESKTOP"),
                    href: "https://github.com/ente-io/ente/tree/main/desktop#readme",
                    target: "_blank",
                },
                {
                    text: t("FOOTER_CLI"),
                    href: "https://github.com/ente-io/ente/tree/main/cli#readme",
                    target: "_blank",
                },
            ],
        },
        {
            heading: t("FOOTER_TECHNOLOGY"),
            links: [
                { text: t("FOOTER_ENCRYPTION"), href: "/architecture" },
                { text: t("FOOTER_REPLICATION"), href: "/reliability" },
                { text: t("FOOTER_MACHINE_LEARNING"), href: "/ml" },
            ],
        },
        {
            heading: t("FOOTER_COMPARE"),
            links: [
                {
                    text: t("FOOTER_GOOGLE_PHOTOS_VS_ENTE"),
                    href: "/compare/ente-vs-google-photos/",
                },
                {
                    text: t("FOOTER_APPLE_PHOTOS_VS_ENTE"),
                    href: "/compare/ente-vs-apple-photos/",
                },
                {
                    text: t("FOOTER_DROPBOX_VS_ENTE"),
                    href: "/compare/ente-vs-dropbox/",
                },
            ],
        },
    ];

    return (
        <div
            className={`${shouldFooterBeBlack ? "bg-black" : "bg-white"} mx-auto mt-56 max-w-[1320px] rounded-t-[32px] p-5 md:rounded-t-[40px] lg:mt-72 lg:rounded-t-[60px]`}
        >
            <RiveComponent
                src="/assets/ducky-cart.riv"
                className="-mt-56 h-64 w-full md:-mt-72 md:h-76"
            />
            <div className="mt-0 flex flex-col gap-4 md:mt-8! md:flex-row md:gap-0">
                {row2Links.map((links, index) => (
                    <Row2 key={links.heading}>
                        <span
                            className={`${shouldFooterBeBlack ? "text-white" : "text-black"} mb-2 text-base font-semibold lg:text-lg`}
                        >
                            {links.heading}
                        </span>
                        {links.links.map((link, index) => (
                            <a
                                key={index}
                                href={link.href}
                                target={link.target}
                                className={`${shouldFooterBeBlack ? "text-[#969696]! hover:text-white!" : "hover:green-text! text-[#ccc]"} mb-2 text-sm no-underline! lg:text-base`}
                            >
                                {link.text}
                                {link.label && <Label>{link.label}</Label>}
                            </a>
                        ))}
                    </Row2>
                ))}
            </div>
            <SocialLanguageHelpAndLegal
                localised={localised}
                language={language}
                shouldFooterBeBlack={shouldFooterBeBlack}
            />
        </div>
    );
};

export default FooterLinks;

/*
  For phones smaller than around iPhone 8 - the social links start wrapping.
  However, initially only one icon wraps over to the next row, which does not
  look visually pleasing.

  As a workaround, when we hit these sized screens, limit the max-width of the
  div so that when the wrapping does occur, multiple icons get wrapped over to
  the next row (instead of only one).
  */
const SocialLinkDiv = styled.div`
    @media (max-width: 340px) {
        max-width: 85%;
    }
`;

// ---- Row 2 -----

interface ILinkProps {
    heading: string;
    links: {
        text: string;
        href?: string;
        label?: string;
        target?: string;
    }[];
}

const Row2 = styled.div.attrs({
    className: "d-flex flex-column align-items-start",
})`
    flex: 1;

    @media (min-width: 1280px) {
        min-width: 200px;
    }
`;

const Label = styled.span`
    padding: 4px 8px;
    background: rgba(43, 189, 94, 0.2);
    color: #000 !important;
    font-size: 10px;
    border-radius: 12px;
    margin-left: 10px;
    font-weight: 700;
`;

// ---- Row 3 -----

interface SocialLanguageHelpAndLegalProps {
    localised?: boolean;
    language?: string;
    shouldFooterBeBlack: boolean;
}

const SocialLanguageHelpAndLegal: React.FC<SocialLanguageHelpAndLegalProps> = ({
    localised,
    language,
    shouldFooterBeBlack,
}) => {
    const { t } = useTranslation();

    return (
        // This row is smaller than the other two, so it needs an extra top
        // margin to make it fit nicer.
        <div className="vstack mt-10 gap-2 md:mt-16">
            <div className="d-flex justify-content-between flex-column flex-lg-row flex-wrap gap-5">
                <div className="d-flex gap-md-4 flex-column flex-lg-row flex-wrap gap-3">
                    <SocialLinkDiv className="d-flex align-items-start align-content-center flex-wrap gap-4">
                        {socialMedia.map((item) => (
                            <ExternalIconLink
                                href={item.url}
                                key={item.name}
                                title={item.name}
                                rel="me"
                                className={`link-footer ${shouldFooterBeBlack ? "text-white/50! hover:text-white/70!" : "text-black/50! hover:text-black/70!"}`}
                            >
                                {item.icon}
                            </ExternalIconLink>
                        ))}
                    </SocialLinkDiv>
                </div>
                <small>
                    <div className="d-flex flex-column flex-md-row flex-wrap gap-3">
                        {localised && (
                            <LanguageSelect
                                currentLanguage={language ?? "en"}
                                shouldFooterBeBlack={shouldFooterBeBlack}
                            />
                        )}
                        <a
                            className={`${shouldFooterBeBlack ? "text-[#ccc]! hover:text-white!" : "hover:green-text! text-[#ccc]"} w-fit no-underline!`}
                            href="https://ente.io/help"
                            target="_blank"
                        >
                            {t("FOOTER_HELP")}
                        </a>
                        <Link
                            className={`${shouldFooterBeBlack ? "text-[#ccc]! hover:text-white!" : "hover:green-text! text-[#ccc]"} w-fit no-underline!`}
                            to="/privacy"
                        >
                            {t("FOOTER_PRIVACY")}
                        </Link>
                        <Link
                            className={`${shouldFooterBeBlack ? "text-[#ccc]! hover:text-white!" : "hover:green-text! text-[#ccc]"} w-fit no-underline!`}
                            to="/terms"
                        >
                            {t("FOOTER_TERMS")}
                        </Link>
                    </div>
                </small>
            </div>
        </div>
    );
};

const LanguageSelect: React.FC<{
    currentLanguage: string;
    shouldFooterBeBlack: boolean;
}> = ({ currentLanguage, shouldFooterBeBlack }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [hoveredLang, setHoveredLang] = useState<string | null>(null);
    const location = useLocation();

    const supportedLanguages = ["en", "de", "fr", "nl", "es", "it", "tr"];

    const getLanguageLabel = (lng: string) => {
        switch (lng) {
            case "en":
                return "English";
            case "de":
                return "Deutsch";
            case "fr":
                return "Français";
            case "nl":
                return "Nederlands";
            case "es":
                return "Español";
            case "it":
                return "Italiano";
            case "tr":
                return "Türkçe";
            default:
                return lng;
        }
    };

    // close dropdown if clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLanguageChange = (lng: string) => {
        localStorage.setItem("chosenLanguage", lng);

        const langPattern = supportedLanguages.join("|");
        const basePath =
            location.pathname
                .replace(new RegExp(`^/(${langPattern})`), "")
                .replace(/\/+$/, "") || "/";

        const newPath =
            lng === "en"
                ? basePath
                : `/${lng}${basePath === "/" ? "" : basePath}`;
        navigate(newPath);
    };

    return (
        <div ref={dropdownRef} className="relative">
            <div
                onClick={() => setIsOpen((prev) => !prev)}
                className={`mr-2 flex w-fit cursor-pointer flex-row items-center gap-1 ${shouldFooterBeBlack ? "text-[#ccc]" : "text-black"} select-none lg:ml-8`}
            >
                <span>{getLanguageLabel(currentLanguage)}</span>
                <span>
                    <ChevronDownIcon
                        className={`mt-0.5 w-3 transform transition-transform duration-100 ${
                            isOpen ? "rotate-180" : ""
                        }`}
                    />
                </span>
            </div>
            {isOpen && (
                <div
                    className={`${shouldFooterBeBlack ? "bg-[#242424]" : "bg-ente-light-gray"} xs:w-[18rem] xs:-top-[950%] absolute -top-[800%] z-[1000] grid w-[15rem] grid-cols-2 rounded-xl py-1 pr-2 lg:-left-[200%]`}
                >
                    {supportedLanguages.map((lng) => {
                        const isCurrentLanguage = currentLanguage === lng;
                        const isHovered = hoveredLang === lng;

                        let bgColor = "";
                        let textColor = "";

                        if (isHovered || isCurrentLanguage) {
                            if (shouldFooterBeBlack) {
                                bgColor = "bg-[#3a3a3a]";
                                textColor = "text-white";
                            } else {
                                bgColor = "bg-white";
                                textColor = "text-gray-800";
                            }
                        }

                        return (
                            <div
                                key={lng}
                                onClick={() => {
                                    setIsOpen(false);
                                    handleLanguageChange(lng);
                                }}
                                onMouseEnter={() => setHoveredLang(lng)}
                                onMouseLeave={() => setHoveredLang(null)}
                                className={`xs:text-sm xs:px-3 xs:py-2 my-1 ml-2 cursor-pointer rounded-lg px-2 py-1.5 text-xs ${shouldFooterBeBlack ? "text-gray-300" : "text-gray-800"} no-underline transition-colors duration-200 ${bgColor} ${textColor}`}
                            >
                                <span
                                    className={`fi fi-${lng === "en" ? "us" : lng} mr-2 text-xs`}
                                ></span>
                                {getLanguageLabel(lng)}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
